/**
 * The in-app event queue both apps use (docs/APP-EVENTS.md; appevents.ts for the rules). One per app: the phone keeps it in its
 * AsyncStorage, the web app in localStorage. It keeps an event only while sharing is on for a signed-in person of a business and
 * the screen may be measured (E1); a tap only by a name a developer wrote (E2). It holds only the events of the person and business
 * that made them (a different sign-in or business empties it), at most 500, and sends batches of 100 with backoff on failure.
 */
import {
  MAX_SCREEN_MS, newSessionId, PATH_GAP, SESSION_IDLE_MS,
  appScreenAllowed, controlIndex, controlName, isCatalogItem, outboundHost, type AppFlow, type AppPlatform, type BuyPlacement, type ControlIndex,
  type ControlList, type FlowStage, type WireBatch, type WireEvent,
} from "./appevents.ts";

/** A key-value store (AsyncStorage, or localStorage wrapped). */
export interface EventKV { getItem(key: string): Promise<string | null>; setItem(key: string, value: string): Promise<void> }

/** 30 s, 1 min, 2 min, … capped at 15 min (the outbox's backoff). */
export const eventBackoffMs = (attempts: number) => Math.min(30_000 * 2 ** Math.max(0, attempts - 1), 15 * 60_000);

export const EVENTS_KEY = "logwave.events.v1";
export const MAX_QUEUED = 500;
export const BATCH_SIZE = 100;
export const FLUSH_EVERY_MS = 30_000;
export const ONCE_KEY = "logwave.events.once.v1";

/** Where events go, and as whom (the session fills it). `who` is only compared, never sent: the server takes the person from the sign-in. */
export interface EventContext {
  enabled: boolean;
  orgId?: string;
  who?: string;
  platform: AppPlatform;
  version: string;
  send?: (orgId: string, batch: WireBatch) => Promise<unknown>;
}

interface Stored { o: string; w: string; e: WireEvent[] }

export class EventTracker {
  private ctx: EventContext = { enabled: false, platform: "ios", version: "0.0.0" };
  private screen: string | null = null;
  private q: WireEvent[] = [];
  private owner = { o: "", w: "" };
  private loaded: Promise<void> | null = null;
  private attempts = 0;
  private nextAt = 0;
  private sending = false;
  /** Null until the app's control list has loaded (a web build fetches it after start-up): taps in that gap wait in `early`. */
  private controls: ControlIndex | null;
  private early: { testID?: string; label?: string; kind?: "button" | "link" }[] = [];
  /* Sessions and time on screen: one session per app open (a new one after SESSION_IDLE_MS in the background or idle). */
  private sid = "";
  private lastAt = 0;
  /** The measured screen before this one in the session ("" none yet; "gap" when it was a screen never measured). */
  private prev = "";
  /** When the current screen's active clock last started; null while paused (background, hidden tab). */
  private since: number | null = null;
  private activeMs = 0;
  private pausedAt: number | null = null;
  constructor(private kv: EventKV | null, controls: ControlList | null, private now: () => number = () => Date.now()) { this.controls = controls ? controlIndex(controls) : null; }
  /** The control list has arrived: taps that waited for it are counted now, by the same rule as any tap. */
  setControls(controls: ControlList): void {
    this.controls = controlIndex(controls);
    const waiting = this.early; this.early = [];
    for (const o of waiting) this.tap(o);
  }

  private load(): Promise<void> {
    if (!this.loaded) {
      this.loaded = (async () => {
        try {
          const s = JSON.parse((await this.kv?.getItem(EVENTS_KEY)) ?? "null") as Stored | null;
          if (s && Array.isArray(s.e)) { this.owner = { o: String(s.o ?? ""), w: String(s.w ?? "") }; this.q = [...s.e, ...this.q].slice(-MAX_QUEUED); }
        } catch { /* a bad store starts empty */ }
      })();
    }
    return this.loaded;
  }
  private persist() { void this.kv?.setItem(EVENTS_KEY, JSON.stringify({ ...this.owner, e: this.q } satisfies Stored)).catch(() => undefined); }

  /** The session's state. Off (or a different person or business): the queue is emptied and nothing more is kept. */
  async configure(ctx: EventContext): Promise<void> {
    await this.load();
    const was = this.on;
    this.ctx = ctx;
    const owner = { o: ctx.orgId ?? "", w: ctx.who ?? "" };
    if (!ctx.enabled || !ctx.orgId || owner.o !== this.owner.o || owner.w !== this.owner.w) { this.q = []; this.attempts = 0; this.nextAt = 0; }
    this.owner = owner;
    // Turned on while a screen is open (the first screen after sign-in): that screen counts as opened now.
    if (!was && this.on && this.current) { this.startClock(); this.push({ k: "view" }); }
    this.persist();
  }

  get on(): boolean { return this.ctx.enabled && !!this.ctx.orgId; }
  /** The screen now, when it may be measured (null on a health screen). */
  get current(): string | null { return this.screen && appScreenAllowed(this.screen) ? this.screen : null; }

  private push(e: Omit<WireEvent, "t" | "s">, keepSession = false): boolean {
    const s = this.current;
    if (!this.on || !s) return false;
    const t = this.now();
    if (!this.sid || (!keepSession && this.lastAt && t - this.lastAt >= SESSION_IDLE_MS)) { this.sid = newSessionId(); if (e.k !== "leave") delete (e as WireEvent).pv; this.prev = ""; }
    if (!keepSession) this.lastAt = t; // a leave is the end of the last thing done, not something new
    this.q.push({ t, s, sid: this.sid, ...e } as WireEvent);
    if (this.q.length > MAX_QUEUED) this.q = this.q.slice(-MAX_QUEUED);
    this.persist();
    return true;
  }

  /** A screen opened (route pattern). A health screen is remembered as "no screen": nothing is kept until another opens. */
  setScreen(screen: string): void {
    if (screen === this.screen) return;
    this.leave();
    const before = this.screen;
    this.screen = screen;
    if (before !== null) this.prev = appScreenAllowed(before) ? before : PATH_GAP; // a health screen is a gap in the path, never its name
    this.startClock();
    const pv = this.prev;
    this.push({ k: "view", ...(pv ? { pv } : {}) });
  }

  private startClock() { this.activeMs = 0; this.since = this.pausedAt === null ? this.now() : null; }
  /** Active time on the current screen so far (paused time never counts), capped at MAX_SCREEN_MS. */
  activeTime(): number { return Math.min(MAX_SCREEN_MS, this.activeMs + (this.since === null ? 0 : this.now() - this.since)); }
  /** The current screen closes: its active time goes as a "leave". */
  private leave() {
    if (!this.current) return;
    const ms = this.activeTime();
    this.since = null; this.activeMs = 0;
    this.push({ k: "leave", ms }, true); // the leave belongs to the session the screen was opened in
  }
  /** The app went to the background or the tab was hidden: the clock stops. */
  pause(): void {
    if (this.pausedAt !== null) return;
    const t = this.now();
    if (this.since !== null) { this.activeMs += t - this.since; this.since = null; }
    this.pausedAt = t;
  }
  /** Back in front: the clock runs again; after SESSION_IDLE_MS away, the screen is closed in the old session and opens a new one. */
  resume(): void {
    if (this.pausedAt === null) return;
    const away = this.now() - this.pausedAt;
    this.pausedAt = null;
    if (away >= SESSION_IDLE_MS && this.current) {
      const ms = Math.min(MAX_SCREEN_MS, this.activeMs);
      this.activeMs = 0;
      this.push({ k: "leave", ms }, true);
      this.sid = ""; this.prev = "";
      this.startClock();
      this.push({ k: "view" });
      return;
    }
    this.since = this.now();
  }
  get session(): string { return this.sid; }
  /** The page is going away (a browser tab closing): the current screen's time goes now. */
  end(): void { this.leave(); this.screen = null; }
  tap(o: { testID?: string; label?: string; kind?: "button" | "link" }): boolean {
    if (!this.controls) { if (this.early.length < 50) this.early.push(o); return false; }
    const c = controlName(this.controls, o);
    return c ? this.push({ k: "tap", c }) : false;
  }
  outbound(href: string, o: { item?: string; placement?: BuyPlacement } = {}): boolean {
    const h = outboundHost(href);
    if (!h) return false;
    return this.push({ k: "out", h, ...(isCatalogItem(o.item) ? { i: o.item } : {}), ...(o.placement ? { pl: o.placement } : {}) });
  }
  answer(kind: "got_it" | "not_yet", item: string, placement: BuyPlacement): boolean {
    return isCatalogItem(item) ? this.push({ k: kind, i: item, pl: placement }) : false;
  }
  flow(f: AppFlow, st: FlowStage): boolean { return this.push({ k: "flow", f, st }); }
  /** A flow step counted once per person on this phone (onboarding: started once, finished once). */
  async flowOnce(f: AppFlow, st: FlowStage): Promise<boolean> {
    if (!this.on || !this.current) return false;
    const key = `${ONCE_KEY}:${this.owner.w}:${this.owner.o}`;
    let seen: string[] = [];
    try { seen = JSON.parse((await this.kv?.getItem(key)) ?? "[]") as string[]; } catch { seen = []; }
    if (seen.includes(`${f}:${st}`)) return false;
    if (!this.flow(f, st)) return false;
    void this.kv?.setItem(key, JSON.stringify([...seen, `${f}:${st}`])).catch(() => undefined);
    return true;
  }

  /** Sends up to one batch when due. Never throws. */
  async flush(force = false): Promise<number> {
    await this.load();
    const { send, orgId } = this.ctx;
    if (!this.on || !send || !orgId || !this.q.length || this.sending) return 0;
    if (!force && this.now() < this.nextAt) return 0;
    const batch = this.q.slice(0, BATCH_SIZE);
    this.sending = true;
    try {
      await send(orgId, { v: 1, p: this.ctx.platform, av: this.ctx.version, e: batch });
      this.q = this.q.slice(batch.length);
      this.attempts = 0; this.nextAt = 0;
      this.persist();
      return batch.length;
    } catch {
      this.attempts++;
      this.nextAt = this.now() + eventBackoffMs(this.attempts);
      return 0;
    } finally { this.sending = false; }
  }

  /** Tests: what is waiting. */
  queued(): WireEvent[] { return this.q.map((e) => ({ ...e })); }
}

